import React, { useEffect, useMemo, useRef, useState } from "react";

const MultiSelect = ({
  label,
  options = [],
  selected = [],
  onChange,
  helper,
  error,
  className = "",
  containerClassName = "",
  searchable = false,
  searchPlaceholder = "Search options...",
  maxSummaryItems = 2,
  compact = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const containerRef = useRef(null);

  const handleToggle = (optionId) => {
    const newSelected = selected.includes(optionId)
      ? selected.filter((id) => id !== optionId)
      : [...selected, optionId];
    onChange(newSelected);
  };

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  const selectedLabels = useMemo(() => {
    return options
      .filter((option) => selected.includes(option.id))
      .map((option) => option.name);
  }, [options, selected]);

  const summary = useMemo(() => {
    if (!selected.length) return "Select options";
    if (!selectedLabels.length) {
      return `${selected.length} selected`;
    }
    const visibleLabels = selectedLabels.slice(0, maxSummaryItems);
    const hiddenCount = selectedLabels.length - visibleLabels.length;
    return `${visibleLabels.join(", ")}${hiddenCount > 0 ? ` ... +${hiddenCount} more` : ""}`;
  }, [maxSummaryItems, selected.length, selectedLabels]);

  const filteredOptions = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    const matchingOptions =
      searchable && query
        ? options.filter((option) =>
            `${option.name || ""}`.toLowerCase().includes(query),
          )
        : options;
    if (!searchable) {
      return matchingOptions;
    }
    return [...matchingOptions].sort(
      (a, b) => Number(selected.includes(b.id)) - Number(selected.includes(a.id)),
    );
  }, [options, searchable, searchQuery, selected]);

  const hiddenSelectedCount = Math.max(0, selectedLabels.length - maxSummaryItems);

  const openDropdown = () => {
    setSearchQuery("");
    setIsOpen(true);
  };

  return (
    <div
      className={[
        "flex min-w-0 flex-col text-slate-600",
        compact ? "gap-1.5 text-xs" : "gap-2 text-sm",
        containerClassName,
      ]
        .filter(Boolean)
        .join(" ")}
      ref={containerRef}
    >
      {label && <label>{label}</label>}
      <div className="relative">
        <div
          className={[
            "flex w-full items-center rounded-lg border bg-white shadow-sm transition",
            compact ? "min-h-10" : "min-h-11",
            className,
            error
              ? "border-rose-300 focus:border-rose-400"
              : "border-slate-200 focus:border-slate-400",
          ]
            .filter(Boolean)
            .join(" ")}
        >
          <button
            type="button"
            onClick={() => setIsOpen((current) => !current)}
            className={[
              "flex min-w-0 flex-1 items-center text-left",
              compact ? "min-h-10 px-3 text-sm" : "min-h-11 px-4 text-base",
            ]
              .filter(Boolean)
              .join(" ")}
            aria-expanded={isOpen}
          >
            <span
              className={[
                "min-w-0 truncate",
                selected.length ? "text-slate-900" : "text-slate-400",
              ]
                .filter(Boolean)
                .join(" ")}
            >
              {summary}
            </span>
          </button>
          {hiddenSelectedCount > 0 && (
            <button
              type="button"
              className="mr-1.5 shrink-0 rounded-md border border-slate-200 px-2 py-1 text-xs font-semibold text-[#4f46e5] transition hover:border-[#4f46e5] hover:bg-indigo-50"
              onClick={openDropdown}
            >
              See more
            </button>
          )}
          <button
            type="button"
            onClick={() => setIsOpen((current) => !current)}
            className={[
              "flex shrink-0 items-center text-slate-400",
              compact ? "min-h-10 px-3 text-sm" : "min-h-11 px-4",
            ]
              .filter(Boolean)
              .join(" ")}
            aria-label={isOpen ? "Close options" : "Open options"}
            aria-expanded={isOpen}
          >
            v
          </button>
        </div>

        {isOpen && (
          <div
            className={[
              "absolute z-20 mt-2 max-h-56 w-full overflow-hidden rounded-lg border bg-white shadow-lg",
              error ? "border-rose-300" : "border-slate-200",
            ]
              .filter(Boolean)
              .join(" ")}
          >
            {searchable && (
              <div className="border-b border-slate-100 p-2">
                <input
                  type="search"
                  value={searchQuery}
                  onChange={(event) => setSearchQuery(event.target.value)}
                  placeholder={searchPlaceholder}
                  className="h-9 w-full rounded-md border border-slate-200 px-3 text-sm text-slate-700 outline-none transition placeholder:text-slate-400 focus:border-slate-400"
                  autoFocus
                />
              </div>
            )}
            <div className="max-h-56 overflow-y-auto p-2">
              {filteredOptions.length === 0 ? (
                <p className="px-2 py-3 text-xs text-slate-400">
                  {searchQuery.trim()
                    ? "No options match your search"
                    : "No options available"}
                </p>
              ) : (
                <div className="space-y-1">
                  {filteredOptions.map((option) => (
                    <label
                      key={option.id}
                      className="flex cursor-pointer items-center gap-2 rounded px-2 py-2 hover:bg-slate-50"
                    >
                      <input
                        type="checkbox"
                        checked={selected.includes(option.id)}
                        onChange={() => handleToggle(option.id)}
                        className="h-4 w-4 rounded border-slate-300 text-[#4f46e5] focus:ring-[#4f46e5]"
                      />
                      <span className="text-sm text-slate-700">{option.name}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      {helper && !error && (
        <span className="text-xs text-slate-500">{helper}</span>
      )}
      {error && <span className="text-xs text-rose-600">{error}</span>}
    </div>
  );
};

export default MultiSelect;
